import React, { useEffect, useMemo, useState } from "react";
import { Alert, Card, Spinner } from "react-bootstrap";
import API from "../services/api";
import "./AdminUserHistoryPanel.css";

const fmtDateTime = (value) =>
  value
    ? new Date(value).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const fmtMoney = (value) => {
  const amount = Number(value || 0);
  if (!amount) return "";
  return `₹${amount.toLocaleString("en-IN")}`;
};

const tone = (value = "") => {
  const status = String(value || "").toLowerCase();
  if (["approved", "paid", "success", "sent", "seen", "completed", "generated"].includes(status)) return "success";
  if (["pending", "draft", "open", "created", "unseen", "clarified"].includes(status)) return "warning";
  if (["rejected", "failed", "cancelled", "plant_rejected"].includes(status)) return "danger";
  return "neutral";
};

const actorLabel = {
  user: "User Side",
  admin: "Admin Side",
  system: "System",
};

const AdminUserHistoryPanel = ({ role, userId, endpoint = "" }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);
  const [actorFilter, setActorFilter] = useState("all");

  useEffect(() => {
    let mounted = true;
    const fetchHistory = async () => {
      try {
        setLoading(true);
        setError("");
        const historyEndpoint = endpoint || `/admin/user-history/${role}/${userId}`;
        const { data } = await API.get(historyEndpoint);
        if (mounted) setPayload(data);
      } catch (err) {
        if (mounted) setError(err?.response?.data?.message || "Failed to load history");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    if (role && userId) fetchHistory();
    return () => {
      mounted = false;
    };
  }, [role, userId, endpoint]);

  const entries = useMemo(() => {
    const rows = Array.isArray(payload?.entries) ? payload.entries : [];
    if (actorFilter === "all") return rows;
    return rows.filter((item) => item.actor === actorFilter);
  }, [payload?.entries, actorFilter]);

  if (loading) {
    return (
      <div className="auhp-loading">
        <Spinner animation="border" />
        <div>Loading history...</div>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  const summary = payload?.summary || {};

  return (
    <div className="auhp-wrap">
      <div className="auhp-summary">
        <div className="auhp-stat">
          <span>Total Events</span>
          <strong>{summary.total || 0}</strong>
        </div>
        <div className="auhp-stat">
          <span>User Side</span>
          <strong>{summary.userActions || 0}</strong>
        </div>
        <div className="auhp-stat">
          <span>Admin Side</span>
          <strong>{summary.adminActions || 0}</strong>
        </div>
        <div className="auhp-stat">
          <span>Payments</span>
          <strong>{summary.payments || 0}</strong>
        </div>
        <div className="auhp-stat">
          <span>Dispatches</span>
          <strong>{summary.dispatches || 0}</strong>
        </div>
      </div>

      <div className="auhp-filters">
        {["all", "user", "admin", "system"].map((value) => (
          <button
            key={value}
            type="button"
            className={`auhp-filter ${actorFilter === value ? "active" : ""}`}
            onClick={() => setActorFilter(value)}
          >
            {value === "all" ? "All" : actorLabel[value]}
          </button>
        ))}
      </div>

      {!entries.length ? (
        <div className="auhp-empty">No history found.</div>
      ) : (
        <div className="auhp-list">
          {entries.map((item, index) => (
            <Card key={`${item.at}-${item.source}-${index}`} className="auhp-card">
              <Card.Body>
                <div className="auhp-head">
                  <div>
                    <div className="auhp-title">{item.title || "History event"}</div>
                    <div className="auhp-sub">
                      {fmtDateTime(item.at)} | {actorLabel[item.actor] || item.actor || "System"} | {item.source || "general"}
                    </div>
                  </div>
                  <div className="auhp-right">
                    {item.amount ? <span className="auhp-amount">{fmtMoney(item.amount)}</span> : null}
                    <span className={`auhp-badge ${tone(item.status)}`}>{item.status || "logged"}</span>
                  </div>
                </div>
                {item.detail ? <div className="auhp-detail">{item.detail}</div> : null}
                {item.ref ? <div className="auhp-ref">Ref: {item.ref}</div> : null}
              </Card.Body>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminUserHistoryPanel;
